import { useEffect } from "react";
import {
  ArrowRight,
  Check,
  X,
  MessageSquare,
  Users,
  Handshake,
  Sparkles,
  Megaphone,
  Rocket,
  Layers,
  LineChart,
  Send,
  ShieldCheck,
  BadgeCheck,
  UserCheck,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ASSETS } from "@/lib/constants";

/* --- helpers -------------------------------------------------- */
function useNoIndex(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const upsertMeta = (attr, name, content) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, prev };
    };
    const robots = upsertMeta("name", "robots", "noindex,nofollow,noarchive,nosnippet");
    const googlebot = upsertMeta("name", "googlebot", "noindex,nofollow");
    const desc = upsertMeta("name", "description", description);
    return () => {
      document.title = prevTitle;
      robots.el.setAttribute("content", robots.prev || "");
      googlebot.el.setAttribute("content", googlebot.prev || "");
      desc.el.setAttribute("content", desc.prev || "");
    };
  }, [title, description]);
}

const ApplyCTA = ({ testid = "mlo-apply-cta", label = "Start my MLO application", variant = "primary" }) => {
  const cls =
    variant === "primary"
      ? "inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-base px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
      : "inline-flex items-center gap-2 bg-white hover:bg-white/95 text-navy-deep font-semibold text-base px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5 ring-2 ring-lime/70";
  return (
    <a data-testid={testid} href="/mlogrowth" className={cls}>
      {label}
      <ArrowRight size={18} />
    </a>
  );
};

const Eyebrow = ({ children }) => (
  <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">{children}</p>
);

const SectionHeading = ({ eyebrow, title, sub, testid }) => (
  <div className="max-w-3xl" data-testid={testid}>
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="mt-4 font-heading font-medium text-3xl sm:text-4xl lg:text-5xl text-balance">
      {title}
    </h2>
    {sub && <p className="mt-5 text-lg text-ink-600 leading-relaxed">{sub}</p>}
  </div>
);

/* --- data ----------------------------------------------------- */

const PLATFORM = [
  {
    icon: <Layers size={22} />,
    title: "A branded MLO page",
    body: "Get your own /yourname landing page you can share with agents, past clients, and prospects — with your photo, NMLS, phone, and application link built in.",
  },
  {
    icon: <Megaphone size={22} />,
    title: "Marketing asset library",
    body: "Ready-to-use flyers, videos, social posts, scripts, and one-pagers. Preview in-browser, share via private link, drop into a text or email in seconds.",
  },
  {
    icon: <Send size={22} />,
    title: "Resource distribution",
    body: "One-tap SMS + email delivery of your apply link, monthly assets, and program info to your agent sphere. Every touchpoint tracked.",
  },
  {
    icon: <Users size={22} />,
    title: "BDM growth engine",
    body: "Activate real estate agents as Business Development Marketers with a scored application flow, monthly verification, and dashboards — your force-multiplier.",
  },
  {
    icon: <LineChart size={22} />,
    title: "Consumer rate handoff",
    body: "The Great Rate Check consumer funnel captures name/email/phone before every application handoff — every lead a followable trail into your pipeline.",
  },
  {
    icon: <Rocket size={22} />,
    title: "NEXA sponsorship & tech stack",
    body: "Qualified MLOs plug into NEXA's wholesale lender network, LO support, workflow, and technology — with Great Rate Check layered on top for growth.",
  },
];

const AVATAR = [
  { icon: <Handshake size={18} />, label: "Licensed and actively originating" },
  { icon: <UserCheck size={18} />, label: "Relationship-driven — Realtors, referrals, sphere" },
  { icon: <BadgeCheck size={18} />, label: "Uses a CRM with daily discipline (or wants to)" },
  { icon: <MessageSquare size={18} />, label: "Prospects and follows up consistently" },
  { icon: <Sparkles size={18} />, label: "Coachable, systems-oriented, professional" },
  { icon: <Rocket size={18} />, label: "Wants to build — not just close another loan" },
];

const NOT_FOR = [
  "You want a place to \"park your license\" and coast on inbound.",
  "You resist CRM, prospecting, or activity accountability.",
  "You avoid working with Realtors or building referral relationships.",
  "You aren't willing to develop and support BDM relationships.",
  "You don't want a structured growth system.",
];

const RUBRIC = [
  { label: "CRM & follow-up discipline", weight: 20 },
  { label: "Realtor / referral relationships", weight: 15 },
  { label: "Business-development consistency", weight: 15 },
  { label: "Current mortgage production", weight: 12 },
  { label: "BDM recruitment / activation potential", weight: 12 },
  { label: "Coachability & system adoption", weight: 10 },
  { label: "Leadership / recruiting ambition", weight: 7 },
  { label: "Mortgage experience", weight: 4 },
  { label: "Current NEXA / GRC affiliation", weight: 3 },
  { label: "Overall application & fit", weight: 2 },
];

const STEPS = [
  {
    n: "01",
    title: "Apply to the MLO Growth Program",
    body: "Complete the short application at /mlogrowth. We look at how you build — not just how much you close.",
  },
  {
    n: "02",
    title: "Fit review + intro conversation",
    body: "If your application scores well, we book a call to align on your goals, market, and growth plan.",
  },
  {
    n: "03",
    title: "NEXA onboarding",
    body: "Qualified MLOs move through NEXA's separate onboarding, licensing, and compliance process to become an active loan officer with the network.",
  },
  {
    n: "04",
    title: "Activate the Great Rate Check platform",
    body: "Get your branded page, plug into the marketing library, launch the BDM growth engine, and start distributing consistently to your sphere.",
  },
];

const FAQS = [
  {
    q: "Is this an offer of employment or sponsorship with NEXA?",
    a: "No. The Great Rate Check MLO Growth Program is a business-development platform. NEXA maintains its own separate employment, licensing, sponsorship, and compliance process, and every MLO must independently satisfy those requirements to originate loans with NEXA.",
  },
  {
    q: "Do I have to be with NEXA already?",
    a: "No — but qualified applicants will be introduced into NEXA's onboarding. If you're already at NEXA, even better; you can activate the Great Rate Check platform right away.",
  },
  {
    q: "How is scoring different from the Partner (BDM) program?",
    a: "We weight CRM discipline, referral relationships, and business-development activity above raw production. A disciplined operator closing 15 loans with 25 active agent relationships and daily CRM habits scores higher than a 40-loan producer running on memory.",
  },
  {
    q: "What does the platform actually cost me?",
    a: "The Great Rate Check platform is included for approved MLOs as part of the growth program. Standard NEXA onboarding, licensing, and per-loan compensation are governed by NEXA's separate agreements.",
  },
  {
    q: "How much time does it take to build the BDM side?",
    a: "Consistent effort matters more than volume. MLOs who dedicate a few focused hours per week to identifying agent partners, running the scripts, and following up tend to activate 3–5 BDMs in their first 90 days.",
  },
  {
    q: "Do I have to build a team or recruit other MLOs?",
    a: "No — but the leadership/recruiting track is available. If you want to build an organization, this platform gives you the tools. If you want to stay solo and just grow your book, that works too.",
  },
];

/* --- page ----------------------------------------------------- */
export default function MLOProgram() {
  useNoIndex(
    "Great Rate Check MLO Growth Program — Build a growth platform",
    "For licensed Mortgage Loan Originators ready to build a Realtor-partnered growth machine — powered by Great Rate Check + NEXA.",
  );

  return (
    <div className="min-h-screen bg-linen text-ink-800 font-sans" data-testid="mlo-program-page">
      {/* Simple top bar */}
      <div className="absolute top-0 inset-x-0 z-20">
        <div className="max-w-6xl mx-auto px-6 pt-6 flex items-center justify-between">
          <a href="/" className="inline-flex bg-white rounded-2xl px-3 py-1.5 shadow">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-9 sm:h-11 w-auto" />
          </a>
          <a
            href="/mlogrowth"
            data-testid="mlo-program-nav-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-sm px-5 py-2.5 rounded-full shadow-cta"
          >
            Start my application
            <ArrowRight size={16} />
          </a>
        </div>
      </div>

      {/* Hero */}
      <section
        data-testid="mlo-program-hero"
        className="relative overflow-hidden pt-28 sm:pt-32 pb-20 sm:pb-28 grain-bg noise text-white"
        style={{ background: "linear-gradient(135deg, #08192F 0%, #0F2A47 45%, #17385e 100%)" }}
      >
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-teal/20 blur-3xl" aria-hidden />
        <div className="absolute -bottom-40 -right-24 h-[26rem] w-[26rem] rounded-full bg-lime/10 blur-3xl" aria-hidden />

        <div className="relative max-w-6xl mx-auto px-6">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-lime">
            Great Rate Check MLO Growth Program
          </p>
          <h1 className="mt-4 font-heading font-medium text-4xl sm:text-6xl leading-[1.02] text-balance max-w-4xl">
            Build more than a pipeline.{" "}
            <span className="italic text-lime">Build a growth platform.</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-white/85 leading-relaxed max-w-3xl">
            For licensed Mortgage Loan Originators ready to build a Realtor-partnered growth
            machine — powered by Great Rate Check + NEXA.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <ApplyCTA testid="mlo-program-hero-cta" label="Start my MLO application" variant="secondary" />
            <a
              href="#platform"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium underline decoration-white/30 underline-offset-4"
            >
              See what you get
            </a>
          </div>
          <p className="mt-6 text-xs text-white/50 max-w-2xl leading-relaxed italic">
            Participation in Great Rate Check does not by itself constitute employment,
            sponsorship, licensing, or approval by NEXA. All applicable NEXA onboarding,
            employment, licensing, and compliance requirements are separate.
          </p>
        </div>
      </section>

      {/* Platform / what you get */}
      <section id="platform" className="py-20 sm:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeading
            eyebrow="The platform"
            title="What you get when you plug in."
            sub="Great Rate Check is not another comp plan. It's an operating system for growing a mortgage business — designed for MLOs who want relationships, systems, and leverage."
            testid="mlo-program-platform-heading"
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PLATFORM.map((p, i) => (
              <div
                key={i}
                data-testid={`mlo-program-platform-${i}`}
                className="rounded-3xl bg-linen border border-linen-border p-6 hover:shadow-soft transition-shadow"
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-white text-teal border border-linen-border">
                  {p.icon}
                </div>
                <h3 className="mt-4 font-heading font-medium text-xl text-navy">{p.title}</h3>
                <p className="mt-2 text-sm text-ink-600 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal MLO / who this is for */}
      <section className="py-20 sm:py-28 bg-linen">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Who this is for"
              title="A relationship-driven MLO with room to grow."
              sub="The most valuable MLO for this program isn't necessarily the highest producer today — it's the disciplined operator ready to build an organization around their mortgage practice."
              testid="mlo-program-avatar-heading"
            />
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {AVATAR.map((a, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 rounded-2xl bg-white border border-linen-border px-4 py-3"
                >
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-teal/10 text-teal">
                    {a.icon}
                  </span>
                  <span className="text-sm text-navy">{a.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Not for you if... */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading
            eyebrow="Straight talk"
            title="This is probably not for you if…"
            sub="We're transparent about who thrives here so you can self-select before you apply."
            testid="mlo-program-notfor-heading"
          />
          <ul className="mt-10 space-y-3">
            {NOT_FOR.map((line, i) => (
              <li key={i} className="flex items-start gap-3 text-navy">
                <span className="mt-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-50 text-red-600 shrink-0">
                  <X size={14} />
                </span>
                <span className="text-base text-ink-700">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Scoring transparency */}
      <section className="py-20 sm:py-28 bg-linen">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading
            eyebrow="How we evaluate"
            title="What we actually score."
            sub="We don't hide the rubric. Here's exactly how we weight applications — CRM discipline and relationships lead, raw production does not dominate."
            testid="mlo-program-rubric-heading"
          />
          <div className="mt-12 rounded-3xl bg-white border border-linen-border p-6 sm:p-8 shadow-soft">
            <ul className="divide-y divide-linen-border">
              {RUBRIC.map((r, i) => (
                <li key={i} className="py-3 flex items-center gap-4">
                  <span className="tabular-nums w-8 text-xs text-ink-500">{i + 1}</span>
                  <span className="flex-1 text-navy text-sm sm:text-base">{r.label}</span>
                  <span className="inline-flex items-center gap-1.5">
                    <div className="w-28 h-1.5 rounded-full bg-linen overflow-hidden">
                      <div
                        className="h-full bg-teal"
                        style={{ width: `${(r.weight / 20) * 100}%` }}
                      />
                    </div>
                    <span className="tabular-nums text-sm font-semibold text-navy w-10 text-right">
                      {r.weight}
                    </span>
                  </span>
                </li>
              ))}
              <li className="pt-4 flex items-center justify-end gap-4 border-t border-linen-border mt-2">
                <span className="text-xs uppercase tracking-widest text-ink-500">Total</span>
                <span className="tabular-nums text-lg font-bold text-navy">100</span>
              </li>
            </ul>
          </div>
          <p className="mt-6 text-sm text-ink-500 max-w-3xl leading-relaxed">
            A 40-loan producer running entirely from memory scores lower than a 15-loan MLO
            with 25 active Realtor relationships, daily CRM habits, and five agents they can
            introduce to the BDM program. We're building around operators, not order-takers.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeading
            eyebrow="How it works"
            title="Four steps from apply to activate."
            testid="mlo-program-steps-heading"
          />
          <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <li
                key={i}
                data-testid={`mlo-program-step-${i}`}
                className="rounded-3xl bg-linen border border-linen-border p-6 relative"
              >
                <span className="text-[11px] font-bold tracking-[0.22em] text-lime">{s.n}</span>
                <h3 className="mt-3 font-heading font-medium text-lg text-navy">{s.title}</h3>
                <p className="mt-2 text-sm text-ink-600 leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28 bg-linen">
        <div className="max-w-3xl mx-auto px-6">
          <SectionHeading
            eyebrow="FAQ"
            title="Straightforward answers."
            testid="mlo-program-faq-heading"
          />
          <div className="mt-10">
            <Accordion type="single" collapsible className="space-y-3" data-testid="mlo-program-faq">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="rounded-2xl bg-white border border-linen-border px-5"
                >
                  <AccordionTrigger className="text-left font-medium text-navy py-4 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-ink-600 text-sm leading-relaxed pb-4">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="py-20 sm:py-28 text-white"
        style={{ background: "linear-gradient(135deg, #08192F 0%, #0F2A47 45%, #17385e 100%)" }}
      >
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Eyebrow>Ready?</Eyebrow>
          <h2 className="mt-4 font-heading font-medium text-3xl sm:text-5xl text-balance">
            Bring the ambition. We'll bring the platform.
          </h2>
          <p className="mt-5 text-white/85 leading-relaxed">
            The application takes about 8 minutes. If you're a fit, we'll book an intro conversation
            within a few business days.
          </p>
          <div className="mt-8">
            <ApplyCTA testid="mlo-program-final-cta" label="Start my MLO application" />
          </div>
          <p className="mt-6 text-xs text-white/50 flex items-center justify-center gap-1.5">
            <ShieldCheck size={12} /> Your information is handled confidentially.
          </p>
        </div>
      </section>
    </div>
  );
}
